import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { DetailHeader, SectionCard, SummaryTile } from "@/components/shared/PageChrome";
import { StageStepper } from "@/components/enquiries/OfferComparison";
import { ClientOfferCard } from "@/components/client/ClientOfferCard";
import { DeclineOfferDialog } from "@/components/offers/DeclineOfferDialog";
import { ClientTimeline } from "@/components/client/ClientTimeline";
import { UrgentChip, UrgentPanel, MarkUrgentDialog } from "@/components/client/UrgentMarker";

const nightsBetween = (a, b) => (a && b ? Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000)) : null);

export default function ClientRequestDetail() {
  const { id } = useParams();
  const [enquiry, setEnquiry] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const isTerminal = !!enquiry && (["COMPLETED", "CANCELLED"].includes(enquiry.status) || !!enquiry.booking_id);
  const load = () => api.get(`/enquiries/${id}`).then((r) => setEnquiry(r.data)).catch(() => setError(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [declining, setDeclining] = useState(null);
  const decide = async (clientOfferId, decision, extra = {}) => {
    if (decision === "DECLINED" && !extra.decline_reason) { setDeclining(clientOfferId); return; }
    try {
      await api.post(`/enquiries/${id}/client-offers/${clientOfferId}/decide`, { decision, ...extra });
      toast.success(`Offer ${decision.toLowerCase()}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record your decision"); if (decision === "DECLINED") throw e; }
  };

  if (error) return <ErrorState testId="client-request-detail-error" />;
  if (!enquiry) return <LoadingState testId="client-request-detail-loading" />;

  const nights = nightsBetween(enquiry.arrival_date, enquiry.departure_date);
  const offers = enquiry.client_offers || [];

  return (
    <div data-testid="client-request-detail-page" className="space-y-5">
      <DeclineOfferDialog open={!!declining} onOpenChange={(v) => !v && setDeclining(null)} onSubmit={(extra) => decide(declining, "DECLINED", extra)} />
      <DetailHeader
        testId="client-request-header"
        crumbs={[{ label: "Requests", to: "/client/requests" }, { label: enquiry.jigsaw_reference }]}
        reference={enquiry.jigsaw_reference}
        chips={<><StatusBadge status={enquiry.status} /><UrgentChip urgent={enquiry.client_urgent} testId="client-request-urgent-chip" /></>}
        title={<>{enquiry.jigsaw_reference}{enquiry.location_text ? ` · ${enquiry.location_text}` : ""}</>}
        meta={[
          { label: "Location", value: enquiry.location_text },
          { label: "Arrival", value: enquiry.arrival_date, mono: true },
          { label: "Departure", value: enquiry.departure_date, mono: true },
          { label: "Rooms / guests", value: `${enquiry.rooms_required ?? "—"} / ${enquiry.guests_count ?? "—"}` },
          enquiry.assigned_staff ? { label: "Jigsaw coordinator", value: enquiry.assigned_staff.name } : null,
        ]}
        actions={<>{!isTerminal && !enquiry.client_urgent?.flagged && <MarkUrgentDialog enquiryId={id} onFlagged={load} />}<button onClick={() => navigate("/client/requests")} data-testid="client-request-back-button" className="sr-only">Back to Requests</button></>}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryTile testId="client-request-tile-duration" label="Duration" value={nights == null ? "—" : `${nights} ${nights === 1 ? "night" : "nights"}`} />
        <SummaryTile testId="client-request-tile-period" label="Stay period" value={<span className="font-mono-ref text-[18px]">{enquiry.arrival_date || "—"} → {enquiry.departure_date || "—"}</span>} />
        <SummaryTile testId="client-request-tile-offers" label="Offers presented" value={offers.length} sub={offers.some((o) => o.client_decision === "ACCEPTED") ? "One offer accepted" : offers.length ? "Awaiting your decision" : "Jigsaw is sourcing"} />
      </div>

      <StageStepper status={enquiry.status} />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
        <UrgentPanel urgent={enquiry.client_urgent} testId="client-urgent-panel" />
        {(enquiry.source === "CLIENT_PORTAL" || enquiry.urgency_notes) && (
          <SectionCard title="Your request" testId="client-request-brief" bodyClassName="px-5 pb-4"
            subtitle={enquiry.source === "CLIENT_PORTAL" && enquiry.created_at ? `Raised by your organisation on ${new Date(enquiry.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : "Details Jigsaw recorded for this request"}>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div><dt className="text-[12px] uppercase tracking-wide text-jigsaw-muted">Lead guest</dt><dd data-testid="client-request-brief-lead" className="text-jigsaw-navy">{enquiry.guest_lead_name || "—"}</dd></div>
              <div><dt className="text-[12px] uppercase tracking-wide text-jigsaw-muted">Postcode</dt><dd className="font-mono-ref text-jigsaw-navy">{enquiry.postcode || "—"}</dd></div>
              <div className="sm:col-span-2"><dt className="text-[12px] uppercase tracking-wide text-jigsaw-muted">Notes to Jigsaw</dt><dd data-testid="client-request-brief-notes" className="whitespace-pre-wrap text-jigsaw-navy">{enquiry.urgency_notes || "—"}</dd></div>
            </dl>
          </SectionCard>
        )}
        <SectionCard title="Offers presented to you" testId="client-offers-panel" bodyClassName="px-5 pb-4"
          subtitle={isTerminal ? `This request is ${enquiry.status.toLowerCase()} - offers can no longer be accepted or declined.` : "Accept exactly one offer; Jigsaw confirms the booking with the supplier."}>
          {offers.length === 0 && <p data-testid="client-offers-empty" className="text-sm text-jigsaw-muted">No offers have been presented yet - Jigsaw is sourcing on your behalf.</p>}
          <div className="grid gap-4 md:grid-cols-2">
            {offers.map((o) => <ClientOfferCard key={o.id} offer={o} isTerminal={isTerminal} onDecide={decide} />)}
          </div>
        </SectionCard>
        {enquiry.booking_id && (
          <div data-testid="client-request-placement-link" className="flex flex-col gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-jigsaw-navy">Jigsaw has confirmed your placement for this request.</p>
            <button type="button" data-testid="client-request-open-placement" onClick={() => navigate(`/client/bookings/${enquiry.booking_id}`)} className="inline-flex h-9 items-center justify-center rounded-md bg-jigsaw-navy px-3.5 text-[13px] font-medium text-white">Open placement</button>
          </div>
        )}
        </div>

        <ClientTimeline events={enquiry.activity} offers={offers} coordinator={enquiry.assigned_staff?.name} />
      </div>
    </div>
  );
}
